import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { BillingDto, MarginDto, SpendDto } from '@rp/contracts';
import { api } from '../lib/api';
import { useSession } from '../hooks/session';
import { Badge, Button, Card, Input, Section, Skeleton } from '../components/ui';
import { formatDate } from '../lib/format';
import { alert, haptic } from '../lib/telegram';

const rub = (value: number): string =>
  `${value.toLocaleString('ru-RU', { maximumFractionDigits: value < 100 ? 2 : 0 })} ₽`;

const tokens = (value: number): string =>
  value >= 1_000_000
    ? `${(value / 1_000_000).toFixed(2)} млн`
    : value >= 1_000
      ? `${(value / 1_000).toFixed(1)} тыс`
      : String(value);

/** Дни в правильном падеже: «1 день», «3 дня», «5 дней». */
function days(count: number): string {
  const abs = Math.abs(count);
  const mod10 = abs % 10;
  const mod100 = abs % 100;
  if (mod10 === 1 && mod100 !== 11) return `${abs} день`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return `${abs} дня`;
  return `${abs} дней`;
}

/**
 * Оплата сервера и расход на ИИ.
 *
 * Администратор видит суммы по тарифу поставщика. Разработчик — те же суммы
 * плюс себестоимость и маржу, чтобы видеть разницу, и может править тарифы,
 * дату оплаты и баланс.
 */
export function BillingPage() {
  const session = useSession();
  const billing = useQuery({ queryKey: ['billing'], queryFn: api.billing });

  if (billing.isLoading) {
    return (
      <div className="mx-auto max-w-lg space-y-3 pt-3">
        <Skeleton className="h-32" />
        <Skeleton className="h-44" />
      </div>
    );
  }

  if (!billing.data) {
    return <p className="p-8 text-center text-[var(--color-hint)]">Не удалось загрузить данные об оплате</p>;
  }

  const data = billing.data;
  const contact = data.supportContact.replace(/^@/, '');

  return (
    <div className="mx-auto max-w-lg pt-3">
      <ServerCard billing={data} contact={contact} />
      <SpendCard billing={data} contact={contact} />

      {data.developer && <DeveloperCard billing={data} />}
      {session.isDeveloper && <DeveloperSettings billing={data} />}
    </div>
  );
}

function ServerCard({ billing, contact }: { billing: BillingDto; contact: string }) {
  const { server } = billing;
  const overdue = server.daysLeft !== null && server.daysLeft < 0;

  return (
    <Section title="Сервер">
      <Card>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="t-body font-medium">{server.label}</p>
            <p className="mt-0.5 t-footnote text-[var(--color-hint)]">{rub(server.costRub)} в месяц</p>
          </div>
          {server.dueSoon && (
            <Badge color={overdue ? 'var(--color-negative)' : 'var(--color-neutral)'}>
              {overdue ? 'просрочено' : 'скоро оплата'}
            </Badge>
          )}
        </div>

        {server.paidUntil ? (
          <div className="mt-3 rounded-[var(--radius-control)] bg-[var(--color-surface)] p-3">
            <p className="t-subhead">
              Оплачен до <b>{formatDate(server.paidUntil)}</b>
            </p>
            <p
              className="mt-0.5 t-footnote"
              style={{
                color: overdue
                  ? 'var(--color-negative)'
                  : server.dueSoon
                    ? 'var(--color-neutral)'
                    : 'var(--color-hint)',
              }}
            >
              {server.daysLeft === null
                ? ''
                : overdue
                  ? `Просрочено на ${days(server.daysLeft)}`
                  : server.daysLeft === 0
                    ? 'Последний день'
                    : `Осталось ${days(server.daysLeft)}`}
            </p>
          </div>
        ) : (
          <p className="mt-3 rounded-[var(--radius-control)] bg-[var(--color-surface)] p-3 t-footnote text-[var(--color-hint)]">
            Дата оплаты не указана.
          </p>
        )}

        <PayButton contact={contact} label="Оплатить сервер" />
        <p className="mt-2 t-caption text-[var(--color-hint)]">
          Напоминание приходит в Telegram за {days(server.remindDaysBefore)} до окончания.
        </p>
      </Card>
    </Section>
  );
}

function SpendCard({ billing, contact }: { billing: BillingDto; contact: string }) {
  const { spend, clientRates, aiBalance } = billing;

  return (
    <Section title="Расход на ИИ">
      <Card>
        <SpendRow title="Этот месяц" spend={spend.currentMonth} highlight />
        <SpendRow title="Прошлый месяц" spend={spend.previousMonth} />
        <SpendRow title="За всё время" spend={spend.total} />

        <p className="mt-3 border-t border-[var(--color-separator)] pt-3 t-caption leading-relaxed text-[var(--color-hint)]">
          Тариф: {rub(clientRates.inputRub)} за 1 млн входных токенов, {rub(clientRates.outputRub)} за
          1 млн выходных.
        </p>

        {aiBalance && (
          <div
            className="mt-3 rounded-[var(--radius-control)] p-3"
            style={{
              background: aiBalance.lowBalance
                ? 'color-mix(in srgb, var(--color-negative) 12%, transparent)'
                : 'var(--color-surface)',
            }}
          >
            <p className="t-subhead">
              Остаток: <b>{rub(aiBalance.remainingRub)}</b> из {rub(aiBalance.balanceRub)}
            </p>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-[var(--color-separator)]">
              <div
                className="h-full rounded-full"
                style={{
                  width: `${Math.min(100, Math.max(0, (aiBalance.remainingRub / aiBalance.balanceRub) * 100))}%`,
                  background: aiBalance.lowBalance ? 'var(--color-negative)' : 'var(--color-positive)',
                }}
              />
            </div>
            {aiBalance.daysLeft !== null && (
              <p className="mt-1.5 t-caption text-[var(--color-hint)]">
                При текущем темпе хватит примерно на {days(aiBalance.daysLeft)}
              </p>
            )}
          </div>
        )}

        <PayButton contact={contact} label="Пополнить токены" />
      </Card>
    </Section>
  );
}

function SpendRow({ title, spend, highlight }: { title: string; spend: SpendDto; highlight?: boolean }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-[var(--color-separator)] py-2.5 last:border-0">
      <div className="min-w-0">
        <p className={highlight ? 't-callout font-medium' : 't-callout'}>{title}</p>
        <p className="t-caption text-[var(--color-hint)]">
          {tokens(spend.promptTokens)} вход · {tokens(spend.completionTokens)} выход · {spend.requests} запр.
        </p>
      </div>
      <p className={highlight ? 't-headline tabular-nums' : 't-callout tabular-nums'}>
        {rub(spend.clientRub)}
      </p>
    </div>
  );
}

/** Себестоимость и маржа — блок виден только разработчику. */
function DeveloperCard({ billing }: { billing: BillingDto }) {
  const dev = billing.developer!;
  const rows: ReadonlyArray<readonly [string, SpendDto, MarginDto]> = [
    ['Этот месяц', billing.spend.currentMonth, dev.currentMonth],
    ['Прошлый месяц', billing.spend.previousMonth, dev.previousMonth],
    ['За всё время', billing.spend.total, dev.total],
  ];

  return (
    <Section title="Только для разработчика">
      <Card>
        <p className="mb-3 t-caption leading-relaxed text-[var(--color-hint)]">
          Себестоимость берётся из ответов Polza AI. Администратор этот блок не видит.
        </p>

        {rows.map(([title, spend, margin]) => (
          <div key={title} className="border-b border-[var(--color-separator)] py-2.5 last:border-0">
            <div className="flex items-baseline justify-between gap-3">
              <p className="t-callout font-medium">{title}</p>
              <p className="t-callout font-semibold tabular-nums text-[var(--color-positive)]">
                +{rub(margin.marginRub)}
              </p>
            </div>
            <div className="mt-1 flex justify-between t-caption text-[var(--color-hint)]">
              <span>
                клиент {rub(spend.clientRub)} · себестоимость {rub(margin.costRub)}
              </span>
              <span>{margin.marginPercent > 0 ? `×${(margin.marginPercent / 100 + 1).toFixed(1)}` : ''}</span>
            </div>
          </div>
        ))}

        <p className="mt-3 border-t border-[var(--color-separator)] pt-3 t-caption leading-relaxed text-[var(--color-hint)]">
          Ваш тариф: {rub(dev.total.rates.inputRub)} / 1 млн запрос, {rub(dev.total.rates.outputRub)} / 1
          млн ответ.
          <br />
          Тариф клиента: {rub(billing.clientRates.inputRub)} / {rub(billing.clientRates.outputRub)}.
        </p>
      </Card>
    </Section>
  );
}

/** Правка тарифов, даты оплаты и баланса. */
function DeveloperSettings({ billing }: { billing: BillingDto }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({
    serverLabel: billing.server.label,
    serverCostRub: String(billing.server.costRub),
    serverPaidUntil: billing.server.paidUntil
      ? new Date(billing.server.paidUntil).toISOString().slice(0, 10)
      : '',
    remindDaysBefore: String(billing.server.remindDaysBefore),
    clientInputRub: String(billing.clientRates.inputRub),
    clientOutputRub: String(billing.clientRates.outputRub),
    costInputRub: String(billing.developer?.total.rates.inputRub ?? ''),
    costOutputRub: String(billing.developer?.total.rates.outputRub ?? ''),
    aiBalanceRub: billing.aiBalance ? String(billing.aiBalance.balanceRub) : '',
    supportContact: billing.supportContact,
  });

  const save = useMutation({
    mutationFn: () =>
      api.updateBilling({
        serverLabel: form.serverLabel,
        serverCostRub: Number(form.serverCostRub) || 0,
        serverPaidUntil: form.serverPaidUntil ? new Date(form.serverPaidUntil) : null,
        remindDaysBefore: Number(form.remindDaysBefore) || 3,
        clientInputRub: Number(form.clientInputRub) || 0,
        clientOutputRub: Number(form.clientOutputRub) || 0,
        costInputRub: Number(form.costInputRub) || 0,
        costOutputRub: Number(form.costOutputRub) || 0,
        aiBalanceRub: form.aiBalanceRub === '' ? null : Number(form.aiBalanceRub),
        supportContact: form.supportContact,
      }),
    onSuccess: (data) => {
      haptic.success();
      queryClient.setQueryData(['billing'], data);
    },
    onError: (error: Error) => {
      haptic.error();
      alert(error.message);
    },
  });

  const field = (key: keyof typeof form, label: string, type = 'text', hint?: string) => (
    <div className="mb-3">
      <label className="mb-1.5 block t-footnote text-[var(--color-hint)]">{label}</label>
      <Input
        type={type}
        inputMode={type === 'number' ? 'decimal' : undefined}
        value={form[key]}
        onChange={(event) => setForm((current) => ({ ...current, [key]: event.target.value }))}
      />
      {hint && <p className="mt-1 t-caption text-[var(--color-hint)]">{hint}</p>}
    </div>
  );

  return (
    <Section title="Настройки оплаты">
      <Card>
        {field('serverPaidUntil', 'Оплачен до', 'date', 'Администратор видит эту дату и остаток дней')}
        {field('remindDaysBefore', 'Напомнить за, дней', 'number')}
        {field('serverLabel', 'Название сервера')}
        {field('serverCostRub', 'Стоимость сервера, ₽/мес', 'number')}

        <div className="mt-4 border-t border-[var(--color-separator)] pt-3">
          <p className="mb-2 t-footnote font-medium">Тариф для клиента, ₽ за 1 млн</p>
          <div className="grid grid-cols-2 gap-2">
            {field('clientInputRub', 'Вход', 'number')}
            {field('clientOutputRub', 'Выход', 'number')}
          </div>

          <p className="mb-2 t-footnote font-medium">Ваша себестоимость, ₽ за 1 млн</p>
          <div className="grid grid-cols-2 gap-2">
            {field('costInputRub', 'Запрос', 'number')}
            {field('costOutputRub', 'Ответ', 'number')}
          </div>
        </div>

        <div className="border-t border-[var(--color-separator)] pt-3">
          {field('aiBalanceRub', 'Баланс на токены, ₽', 'number', 'Пусто — не отслеживать остаток')}
          {field('supportContact', 'Telegram для оплаты', 'text', 'Без @, например thiskai')}
        </div>

        <Button className="w-full" loading={save.isPending} onClick={() => save.mutate()}>
          Сохранить
        </Button>
      </Card>
    </Section>
  );
}

function PayButton({ contact, label }: { contact: string; label: string }) {
  return (
    <a
      href={`https://t.me/${contact}`}
      target="_blank"
      rel="noreferrer noopener"
      onClick={() => haptic.tap()}
      className="mt-3 flex min-h-11 w-full items-center justify-center rounded-[var(--radius-control)] bg-[var(--color-accent)] px-4 t-callout font-medium text-[var(--color-on-accent)] active:opacity-80"
    >
      {label} — @{contact}
    </a>
  );
}
